import type { Invoice as ApiInvoice, InvoiceDetail, InvoiceListQuery, InvoiceSortKey, InvoiceStatusChange } from "@shyne/shared/api";
import { invoice_statuses, type Invoice } from "@/models/invoice.model";
import type { InvoiceSubjectRelations } from "@/lib/invoices/invoice-subject";

export type InvoiceListJoin = Omit<Invoice, "created_at" | "updated_at"> & InvoiceSubjectRelations & { vendor_name: string };

export const LISTED_INVOICE_STATUSES = [invoice_statuses.draft, invoice_statuses.sent, invoice_statuses.paid, invoice_statuses.void] as const;

export type ListedInvoiceStatus = (typeof LISTED_INVOICE_STATUSES)[number];

export type ChangedInvoiceStatus = invoice_statuses.sent | invoice_statuses.paid | invoice_statuses.void;

const STATUS_CHANGES: Record<ChangedInvoiceStatus, InvoiceStatusChange> = {
  [invoice_statuses.sent]: "sent",
  [invoice_statuses.paid]: "paid",
  [invoice_statuses.void]: "void",
};

export const toInvoiceStatusChange = (status: ChangedInvoiceStatus): InvoiceStatusChange => STATUS_CHANGES[status];

export interface InvoiceListFilters {
  contract?: string;
  status?: ListedInvoiceStatus;
  search?: string;
  issueFrom?: string;
  issueTo?: string;
  dueFrom?: string;
  dueTo?: string;
  invoiceNumberFrom?: number;
}

export type InvoiceListSortKey = "invoice_number" | "issue_date" | "due_date" | "period_start";

const SORT_KEYS: Record<InvoiceListSortKey, InvoiceSortKey> = {
  invoice_number: "number",
  issue_date: "issuedAt",
  due_date: "dueAt",
  period_start: "periodStart",
};

export type InvoiceListPage = {
  page: number;
  pageSize: number;
  sortKey?: string;
  sortOrder: "asc" | "desc";
  actingOrganization?: string;
};

export function toInvoiceListQuery(filters: InvoiceListFilters = {}, { page, pageSize, sortKey, sortOrder, actingOrganization }: InvoiceListPage): Partial<InvoiceListQuery> {
  const search = filters.search?.trim();
  return {
    actingOrganization,
    contract: filters.contract,
    status: filters.status,
    ...(search ? { search } : {}),
    issuedFrom: filters.issueFrom,
    issuedTo: filters.issueTo,
    dueFrom: filters.dueFrom,
    dueTo: filters.dueTo,
    numberFrom: filters.invoiceNumberFrom,
    sort: SORT_KEYS[sortKey as InvoiceListSortKey] ?? "number",
    order: sortOrder,
    page,
    pageSize,
  };
}

const propertyOf = (property: ApiInvoice["billed"]["property"]) => (property === null ? null : { id: property.id, name: property.name });

function subjectOf({ billed }: ApiInvoice): Pick<InvoiceListJoin, "appointment" | "daily_work" | "period_start" | "period_end" | "appointment_data" | "contract_data"> {
  if (billed.kind === "contract") {
    return {
      appointment: null,
      daily_work: billed.id,
      period_start: billed.period.start,
      period_end: billed.period.end,
      appointment_data: null,
      contract_data: { id: billed.id, property: propertyOf(billed.property) },
    };
  }
  return {
    appointment: billed.id,
    daily_work: null,
    period_start: null,
    period_end: null,
    appointment_data: {
      id: billed.id,
      property: propertyOf(billed.property),
      unit: billed.unit === null ? null : { unit_number: billed.unit },
      service: billed.service === null ? null : { name: billed.service.name, parent: billed.service.category === null ? null : { name: billed.service.category } },
      ...(billed.pay === undefined ? {} : { subs_payment: billed.pay.subcontractor, individual_payment: billed.pay.worker }),
    },
    contract_data: null,
  };
}

export const toInvoiceListRow = (invoice: ApiInvoice): InvoiceListJoin => ({
  id: invoice.id,
  invoice_number: invoice.number,
  status: invoice.status as invoice_statuses,
  vendor: invoice.vendor.id,
  vendor_name: invoice.vendor.name,
  client: invoice.customer.id,
  amount: invoice.amount,
  issue_date: invoice.issuedAt,
  due_date: invoice.dueAt,
  paid_at: invoice.paidAt,
  ...subjectOf(invoice),
});

export type InvoiceRecordLineItem = {
  id: string;
  particular: string;
  description: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
};

export type InvoiceRecordVendor = {
  id: string;
  name: string;
  phone: string | null;
  location: string | null;
  logo: string | null;
};

export type InvoiceRecordBillTo = {
  name: string;
  address: string | null;
};

export type InvoiceRecord = InvoiceListJoin & {
  vendor_data: InvoiceRecordVendor;
  bill_to: InvoiceRecordBillTo;
  billing_email: string | null;
  line_items: InvoiceRecordLineItem[];
};

function withWorker(row: InvoiceListJoin, worker: InvoiceDetail["worker"]): InvoiceListJoin {
  if (row.appointment_data == null) return row;
  return { ...row, appointment_data: { ...row.appointment_data, assigned_user: worker === null ? null : { name: worker.name } } };
}

export const toInvoiceRecord = (detail: InvoiceDetail): InvoiceRecord => ({
  ...withWorker(toInvoiceListRow(detail), detail.worker),
  vendor_data: { id: detail.vendor.id, name: detail.vendor.name, phone: detail.vendor.phone, location: detail.vendor.location, logo: detail.vendor.logo },
  bill_to: { name: detail.billTo.name, address: detail.billTo.address ?? null },
  billing_email: detail.billingEmail,
  line_items: detail.lineItems.map((line) => ({
    id: line.id,
    particular: line.item,
    description: line.description,
    quantity: line.quantity,
    unit_price: line.unitPrice,
    total_price: line.total,
  })),
});
